/**
 * Collection Vehicle URL Manager
 * Handles URL manipulation and navigation for vehicle filtering on collection and search pages
 */
class CollectionVehicleURL {
  static FILTER_PARAMS = {
    VEHICLE_FILTER_PREFIX: 'filter.p.m.vehicle.list',
    YEAR: 'filter.p.m.custom.year',
    MODEL: 'filter.p.m.custom.model'
  };

  constructor() {
    this.currentUrl = null;
    this.currentParams = null;
    this.updateCurrentUrl();
  }

  /**
   * Update internal URL state
   */
  updateCurrentUrl() {
    if (typeof window !== 'undefined') {
      this.currentUrl = new URL(window.location.href);
      this.currentParams = new URLSearchParams(this.currentUrl.search);
    }
  }

  /**
   * Check if current page is a collection page
   */
  isCollectionPage() {
    if (!this.currentUrl) return false;
    return this.currentUrl.pathname.startsWith('/collections/');
  }

  /**
   * Check if current page is a search page
   */
  isSearchPage() {
    if (!this.currentUrl) return false;
    return this.currentUrl.pathname.startsWith('/search');
  }

  /**
   * Check if current page supports vehicle filtering (collection or search)
   */
  isVehicleFilterPage() {
    return this.isCollectionPage() || this.isSearchPage();
  }

  /**
   * Get collection handle from current URL
   */
  getCollectionHandle() {
    if (!this.isCollectionPage()) return null;
    
    const pathParts = this.currentUrl.pathname.split('/').filter(Boolean);
    return pathParts[pathParts.length - 1] || null;
  }

  /**
   * Get search query from current URL
   */
  getSearchQuery() {
    if (!this.isSearchPage() || !this.currentParams) return null;
    
    return this.currentParams.get('q') || null;
  }

  /**
   * Check if URL has vehicle filter parameters
   */
  hasVehicleFilters() {
    if (!this.currentParams) return false;

    for (const [key] of this.currentParams) {
      if (key.startsWith(CollectionVehicleURL.FILTER_PARAMS.VEHICLE_FILTER_PREFIX)) {
        return true;
      }
    }
    
    return false;
  }

  /**
   * Get all vehicle filter parameters from URL
   */
  getVehicleFilters() {
    const filters = {};
    
    if (!this.currentParams) return filters;

    for (const [key, value] of this.currentParams) {
      if (key.startsWith(CollectionVehicleURL.FILTER_PARAMS.VEHICLE_FILTER_PREFIX)) {
        filters[key] = decodeURIComponent(value);
      }
      else if (key === CollectionVehicleURL.FILTER_PARAMS.YEAR || 
               key === CollectionVehicleURL.FILTER_PARAMS.MODEL) {
        filters[key] = decodeURIComponent(value);
      }
    }
    
    return filters;
  }

  /**
   * Remove vehicle filter parameters from URL
   */
  removeVehicleFilters() {
    if (!this.currentParams) return this.currentUrl.href;

    const cleanParams = new URLSearchParams();
    
    // Copy non-vehicle filter parameters
    for (const [key, value] of this.currentParams) {
      if (!this.isVehicleFilterParam(key)) {
        cleanParams.append(key, value);
      }
    }
    
    // Build clean URL
    const cleanUrl = `${this.currentUrl.pathname}${cleanParams.toString() ? '?' + cleanParams.toString() : ''}`;
    
    return cleanUrl;
  }

  /**
   * Check if parameter key is a vehicle filter
   */
  isVehicleFilterParam(key) {
    return key.startsWith(CollectionVehicleURL.FILTER_PARAMS.VEHICLE_FILTER_PREFIX) ||
           key === CollectionVehicleURL.FILTER_PARAMS.YEAR ||
           key === CollectionVehicleURL.FILTER_PARAMS.MODEL;
  }

  /**
   * Navigate to URL with vehicle filters removed
   */
  navigateToCleanUrl() {
    const cleanUrl = this.removeVehicleFilters();

    console.log('🔄 Navigating to clean URL:', cleanUrl);

    if (typeof window !== 'undefined') {
      const facetForm = document.querySelector('#FacetFiltersForm');

      if (facetForm) {
        const currentParams = new URLSearchParams(window.location.search);

        // Collect all vehicle filter keys first to avoid iterator mutation issues
        const keysToDelete = [];
        for (const [key, value] of currentParams) {
          if (this.isVehicleFilterParam(key)) {
            keysToDelete.push(key);
          }
        }

        // Then delete them
        keysToDelete.forEach(key => currentParams.delete(key));

        const newUrl = `${window.location.pathname}?${currentParams.toString()}`;

        // Use direct navigation instead of dispatching event to avoid re-adding filters from storage
        window.location.href = newUrl;
      } else {
        window.location.href = cleanUrl;
      }
    }

    return cleanUrl;
  }

  /**
   * Add vehicle filter parameters to URL (without navigation)
   */
  addVehicleFilters(vehicleData) {
    if (!this.currentParams || !vehicleData) return this.currentUrl.href;

    const newParams = new URLSearchParams(this.currentParams);
    
    // Add metaobject filter if available
    if (vehicleData.metaobjectId && vehicleData.searchField && vehicleData.detailsId !=='none') {
      const filterKey = `filter.p.m.${vehicleData.searchField}`;
      newParams.set(filterKey, encodeURIComponent(vehicleData.metaobjectId));
    }
    
    // Add year and model parameters for fallback compatibility
    if (vehicleData.year) {
      newParams.set(CollectionVehicleURL.FILTER_PARAMS.YEAR, encodeURIComponent(vehicleData.year));
    }
    
    if (vehicleData.model) {
      newParams.set(CollectionVehicleURL.FILTER_PARAMS.MODEL, encodeURIComponent(vehicleData.model));
    }
    
    // Ensure sort_by parameter exists
    if (!newParams.has('sort_by')) {
      newParams.set('sort_by', 'best-selling');
    }
    
    const filteredUrl = `${this.currentUrl.pathname}?${newParams.toString()}`;
    
    return filteredUrl;
  }

  /**
   * Navigate to URL with vehicle filters applied
   */
  navigateWithVehicleFilters(vehicleData) {
    const filteredUrl = this.addVehicleFilters(vehicleData);
    
    console.log('🔄 Navigating to filtered URL:', filteredUrl);
    
    if (typeof window !== 'undefined') {
      window.location.href = filteredUrl;
    }
    
    return filteredUrl;
  }

  /**
   * Update URL parameters without page reload
   */
  updateUrlParams(params, replaceState = true) {
    if (typeof window === 'undefined') return;

    const newUrl = new URL(this.currentUrl);
    
    // Update parameters
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        newUrl.searchParams.set(key, value);
      } else {
        newUrl.searchParams.delete(key);
      }
    });
    
    // Update browser URL
    if (replaceState) {
      window.history.replaceState({}, '', newUrl.toString());
    } else {
      window.history.pushState({}, '', newUrl.toString());
    }
    
    // Update internal state
    this.updateCurrentUrl();
  }

  /**
   * Get current URL search parameters as object
   */
  getSearchParams() {
    const params = {};
    
    if (this.currentParams) {
      for (const [key, value] of this.currentParams) {
        params[key] = value;
      }
    }
    
    return params;
  }

  /**
   * Build collection filter URL
   */
  buildCollectionFilterUrl(vehicleData, collectionHandle = null) {
    const handle = collectionHandle || this.getCollectionHandle();
    if (!handle) return null;

    const baseUrl = `/collections/${handle}`;
    return this.buildFilterUrl(baseUrl, vehicleData, true);
  }

  /**
   * Build search filter URL
   */
  buildSearchFilterUrl(vehicleData, searchQuery = null) {
    const baseUrl = '/search';
    return this.buildFilterUrl(baseUrl, vehicleData, false, searchQuery);
  }

  /**
   * Build filter URL for current page type (automatically detects collection or search)
   */
  buildCurrentPageFilterUrl(vehicleData, options = {}) {
    if (this.isCollectionPage()) {
      return this.buildCollectionFilterUrl(vehicleData, options.collectionHandle);
    } else if (this.isSearchPage()) {
      return this.buildSearchFilterUrl(vehicleData, options.searchQuery);
    }
    
    // Fallback: try to build URL for current path
    const baseUrl = this.currentUrl?.pathname || '/';
    return this.buildFilterUrl(baseUrl, vehicleData, this.isCollectionPage(), options.searchQuery);
  }

  /**
   * Build filter URL for any page type (collection or search)
   */
  buildFilterUrl(baseUrl, vehicleData, includeSort = true, searchQuery = null) {
    const params = new URLSearchParams();
    
    // Add vehicle filters
    if (vehicleData) {
      if (vehicleData.metaobjectId && vehicleData.searchField && vehicleData.detailsId !== 'none') {
        const filterKey = `filter.p.m.${vehicleData.searchField}`;
        params.set(filterKey, encodeURIComponent(vehicleData.metaobjectId));
        params.delete(CollectionVehicleURL.FILTER_PARAMS.YEAR);
        params.delete(CollectionVehicleURL.FILTER_PARAMS.MODEL);
      }
      
      if (vehicleData.year) {
        params.set(CollectionVehicleURL.FILTER_PARAMS.YEAR, encodeURIComponent(vehicleData.year));
      }
      
      if (vehicleData.model) {
        params.set(CollectionVehicleURL.FILTER_PARAMS.MODEL, encodeURIComponent(vehicleData.model));
      }
    }
    
    // Add search query for search pages
    if (searchQuery) {
      params.set('q', encodeURIComponent(searchQuery));
    }
    
    // Preserve existing non-vehicle parameters
    if (this.currentParams) {
      for (const [key, value] of this.currentParams) {
        if (!this.isVehicleFilterParam(key)) {
          // For search pages, preserve existing search query if no new one provided
          if (!searchQuery || key !== 'q') {
            params.set(key, value);
          }
        }
      }
    }
    
    // Ensure sort_by parameter for collection pages
    if (includeSort && !params.has('sort_by')) {
      params.set('sort_by', 'best-selling');
    }
    
    return `${baseUrl}?${params.toString()}`;
  }

  /**
   * Extract vehicle data from current URL
   */
  extractVehicleDataFromUrl() {
    const vehicleData = {
      metaobjectId: null,
      searchField: null,
      year: null,
      model: null
    };
    
    if (!this.currentParams) return vehicleData;

    // Look for vehicle filter parameters
    for (const [key, value] of this.currentParams) {
      if (key.startsWith(CollectionVehicleURL.FILTER_PARAMS.VEHICLE_FILTER_PREFIX)) {
        vehicleData.searchField = key.replace('filter.p.m.', '');
        vehicleData.metaobjectId = decodeURIComponent(value);
      }
      else if (key === CollectionVehicleURL.FILTER_PARAMS.YEAR) {
        vehicleData.year = decodeURIComponent(value);
      }
      else if (key === CollectionVehicleURL.FILTER_PARAMS.MODEL) {
        vehicleData.model = decodeURIComponent(value);
      }
    }
    
    return vehicleData;
  }

  /**
   * Check if URL is valid for navigation
   */
  isValidUrl(url) {
    if (!url || typeof url !== 'string') return false;
    
    // Allow relative URLs
    if (url.startsWith('/') || url.startsWith('#')) {
      return true;
    }
    
    // Check absolute URLs
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Safe navigation to URL
   */
  navigateTo(url) {
    if (!this.isValidUrl(url)) {
      console.warn('Invalid URL for navigation:', url);
      return false;
    }
    
    if (typeof window !== 'undefined') {
      window.location.href = url;
      return true;
    }
    
    return false;
  }

  /**
   * Get debug information about current URL state
   */
  getDebugInfo() {
    return {
      currentUrl: this.currentUrl?.href || null,
      pathname: this.currentUrl?.pathname || null,
      searchParams: this.getSearchParams(),
      isCollectionPage: this.isCollectionPage(),
      isSearchPage: this.isSearchPage(),
      isVehicleFilterPage: this.isVehicleFilterPage(),
      collectionHandle: this.getCollectionHandle(),
      searchQuery: this.getSearchQuery(),
      hasVehicleFilters: this.hasVehicleFilters(),
      vehicleFilters: this.getVehicleFilters(),
      extractedVehicleData: this.extractVehicleDataFromUrl()
    };
  }

  /**
   * Refresh internal URL state (useful after navigation)
   */
  refresh() {
    this.updateCurrentUrl();
    console.log('🔄 Collection vehicle URL state refreshed');
  }
}

// Export for use in other modules
if (typeof window !== 'undefined') {
  window.CollectionVehicleURL = CollectionVehicleURL;
}